import {useCallback, useEffect, useState} from 'react';
import './role-permissions.professional-caregiver.scss';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {Box, Button, Card, Grid, Stack, Skeleton, Typography, List, ListItem} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {Controller, DefaultValues, FieldPath, useForm} from 'react-hook-form';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {LoadingContent} from '../../../../../../../components/loading-content/loading-content';
import {LocalizedStringInput} from '../../../../../../../components/office/localized-string-input';
import {RouterPrompt} from '../../../../../../../components/router-prompt/router-prompt';
import Switch from '../../../../../../../components/switch/switch';
import {Role} from '../../../../../../../backend/interfaces/role';
import {PutRole} from '../../../../../../../backend/requests/put-role';
import {LocalizedString} from '../../../../../../../backend/interfaces/localized-string';
import {Permission} from '../../../../../../../backend/interfaces/permission';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {PostRole} from '../../../../../../../backend/requests/post-role';
import {RolesRepository} from '../../../../../../../repositories/roles';
import {
  nullifyLocalizedString,
  sanitizeLocalizedString,
} from '../../../../../../../utils/functions/sanitize-localized-string';
import {DEFAULT_FORM_VALIDATIONS, ROLES_MAX_CHARACTERS} from '../../../../../../../utils/constants';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {useObserveViewport} from '../../../../../../../hooks';
import {ScreenPaths} from '../../../../../../../enums';
import {convertArrayToIndex, titleCase} from '../../../../../../../utils/functions';
import {Pallete} from '../../../../../../../theme/bluebird';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {Tooltip} from '../../../../../../../components/tooltip/tooltip';
import {CardCollapse} from '../../../../../../../components/shared/card-collapse';

const ORDERED_SECTIONS = [
  'overview',
  'seniors',
  'team',
  'digital-inclusion',
  'occurrences',
  'settings',
  'daily-care-log',
  'calendar',
] as const;

type RolePermissionsSectionType = (typeof ORDERED_SECTIONS)[number];

type UIPermissionsSections = Record<RolePermissionsSectionType, Permission[]>;

let DEFAULT_PERMISSIONS: Permission[] = Object.values(RolePermissionType)
  .map((type) => ({
    type,
    canRead: false,
    canWrite: false,
  }))
  .filter(
    (permission) =>
      // hide activity sector settings permissions while we don't have this pages
      permission.type !== RolePermissionType.Settings_BADL &&
      permission.type !== RolePermissionType.Settings_IADL &&
      permission.type !== RolePermissionType.Settings_ClinicalSupport &&
      permission.type !== RolePermissionType.Settings_PersonalDevelopment
  );

const DEFAULT_PERMISSION_TYPES = DEFAULT_PERMISSIONS.map(({type}) => type);

// Digital Inclusion permissions to limit access if the user don't have access to digital inclusion module
const DIGITAL_INCLUSION_PERMISSIONS = [
  RolePermissionType.DigitalInclusion_Applications,
  RolePermissionType.DigitalInclusion_Cards,
  RolePermissionType.DigitalInclusion_Devices,
  RolePermissionType.DigitalInclusion_Playlists,
  RolePermissionType.DigitalInclusion_Media,
  RolePermissionType.DigitalInclusion_Services,
  RolePermissionType.Seniors_DigitalInclusion_Applications,
  RolePermissionType.Seniors_DigitalInclusion_Devices,
  RolePermissionType.Seniors_DigitalInclusion_Playlists,
  RolePermissionType.Seniors_DigitalInclusion_Cards,
  RolePermissionType.Team_Professionals_Applications,
  RolePermissionType.Team_Professionals_Cards,
  RolePermissionType.Team_Professionals_Playlists,
];
// Digital Care Management permissions to limit access if the user don't have access to digital care management module
const DIGITAL_CARE_MANAGEMENT_PERMISSIONS = [
  RolePermissionType.Seniors_BADL,
  RolePermissionType.Seniors_IADL,
  RolePermissionType.Seniors_ClinicalSupport,
  RolePermissionType.DailyCareLog_BADL,
  RolePermissionType.DailyCareLog_IADL,
  RolePermissionType.DailyCareLog_ClinicalSupport,
  RolePermissionType.Seniors_CareAndActivities_BADL,
  RolePermissionType.Seniors_CareAndActivities_IADL,
  RolePermissionType.Seniors_CareAndActivities_ClinicalSupport,
  RolePermissionType.Calendar_BADL,
  RolePermissionType.Calendar_IADL,
  RolePermissionType.Calendar_ClinicalSupport,
  RolePermissionType.Occurrences,
  RolePermissionType.Settings_Occurrences,
  RolePermissionType.DailyCareLog_Occurrences,
  RolePermissionType.Team_CareServices,
  RolePermissionType.Team_Professionals_CareServices,
  RolePermissionType.Seniors_CareServices,
];

interface FormData {
  name: string;
  description: string;
  nameLocalized?: LocalizedString;
  descriptionLocalized?: LocalizedString;
  permissions: Record<RolePermissionType, Permission>;
}

const SET_VALUE_SIDE_EFFECTS = {shouldDirty: true, shouldValidate: true};

const RolePermissionsProfessionalCaregiverOfficeScreen = ({mode}: {mode: 'create' | 'edit'}) => {
  const [role, setRole] = useState<Role | undefined>(undefined);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {t} = useTranslation();
  const snackbarContext = useSnackBar();
  const location = useLocation();
  let {url} = useRouteMatch();
  const history = useHistory();
  const {isRunningMobile} = useObserveViewport();
  const {entityId, roleId}: {entityId: string; roleId?: string} = useParams();
  const {
    state: {session: authSession},
    refreshSessionData,
  } = useAuth();

  const SECTIONED_PERMISSIONS: UIPermissionsSections = ORDERED_SECTIONS.reduce(
    (acc, sectionName) => {
      acc[sectionName] = DEFAULT_PERMISSIONS.filter(
        (permission) => permission.type.split(/\/(.*)/, 2)[0] === sectionName
      );

      return acc;
    },
    {} as UIPermissionsSections
  );
  const {
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
    watch,
    reset,
    formState: {isDirty, isValid, errors},
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      nameLocalized: {
        ptPT: '',
        enUS: '',
        esES: '',
      },
      description: '',
      descriptionLocalized: {
        ptPT: '',
        enUS: '',
        esES: '',
      },
      permissions: convertArrayToIndex(DEFAULT_PERMISSIONS, 'type'),
    },
    ...DEFAULT_FORM_VALIDATIONS,
  });

  const fetchRole = useCallback(async () => {
    try {
      const role = await RolesRepository.instance.getRole(entityId, roleId!);
      setRole(role);
      setIsLoadingRole(false);
    } catch {
      snackbarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.role_permissions_professional_caregiver_office_screen.snackbar.load.error
        ),
      });

      history.push(`${url.replace(`/${roleId}`, '')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, roleId, history, t, url]);

  // Get Role only if in edit mode
  useEffect(() => {
    if (mode === 'edit') {
      setIsLoadingRole(true);
      fetchRole();
    }
  }, [mode, fetchRole]);

  const handleCancel = () =>
    history.push(
      `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`
    );

  const handleSubmit = () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const data = getValues();

    let updateData: PutRole | PostRole = {
      name: data.name,
      permissions: Object.values(data.permissions).flat(),
    };

    if (data.description) updateData.description = data.description;

    if (mode === 'edit') {
      // Check and nullify localized strings
      if (data.nameLocalized) updateData.nameLocalized = nullifyLocalizedString(data.nameLocalized);

      if (data.descriptionLocalized)
        updateData.descriptionLocalized = nullifyLocalizedString(data.descriptionLocalized);
    } else {
      // Check and sanitaze localized strings
      if (data.nameLocalized)
        updateData.nameLocalized = sanitizeLocalizedString(data.nameLocalized);

      if (data.descriptionLocalized)
        updateData.descriptionLocalized = sanitizeLocalizedString(data.descriptionLocalized);
    }

    if (mode === 'edit') {
      RolesRepository.instance
        .updateRole(entityId, role?.id!, updateData as PutRole)
        .then(() => {
          snackbarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(
              translations.role_permissions_professional_caregiver_office_screen.snackbar.update
                .success
            ),
          });

          reset(undefined, {keepValues: true, keepDirty: false, keepDefaultValues: false});

          // Update the user session because his permissions might have changed.
          try {
            refreshSessionData(
              authSession?.selectedRelationship?.id,
              authSession?.selectedAffiliation?.id
            );
          } catch (error) {
            // Error
            console.debug('🔴 Could not refresh session.', error);
          }
        })
        .catch(() => {
          snackbarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(
              translations.role_permissions_professional_caregiver_office_screen.snackbar.update
                .error
            ),
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      RolesRepository.instance
        .createRole(entityId, updateData as PostRole)
        .then(() => {
          snackbarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(
              translations.role_permissions_professional_caregiver_office_screen.snackbar.update
                .success
            ),
          });

          history.push(
            `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_RolesAndPermissions}`
          );
        })
        .catch(() => {
          snackbarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(
              translations.role_permissions_professional_caregiver_office_screen.snackbar.update
                .error
            ),
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const fillFormValues = useCallback(() => {
    if (!role) return;

    const {name, description, nameLocalized, descriptionLocalized, permissions} = role;

    // Apply the role permissions to the form state, falling back to the default permission values
    let rolePermissions: Partial<Record<RolePermissionType, Permission>> = {};
    if (permissions) {
      rolePermissions = convertArrayToIndex(permissions, 'type');
    }

    const activePermissions = DEFAULT_PERMISSIONS.reduce(
      (acc, defaultPermission) => {
        acc[defaultPermission.type] = {
          ...defaultPermission,
          ...rolePermissions[defaultPermission.type],
        };

        return acc;
      },
      {} as Record<RolePermissionType, Permission>
    );

    reset({
      name,
      description,
      nameLocalized,
      descriptionLocalized,
      permissions: activePermissions,
    });
  }, [reset, role]);

  const [nameValue] = watch(['name', 'description', 'nameLocalized', 'descriptionLocalized']);

  const handleLocalizedInputValidation = (
    value: string | null | undefined,
    field: 'name' | 'description',
    language?: keyof LocalizedString
  ) => {
    // Check if change is from the default field or the localized value
    const localizedField: FieldPath<DefaultValues<Omit<FormData, 'permissions'>>> = language
      ? `${field}Localized.${language}`
      : field;

    // Get for field max validation
    const fieldMaxLength = ROLES_MAX_CHARACTERS[field];

    // Required validation
    if (localizedField === 'name') {
      if (value?.length === 0) {
        setError(
          localizedField,
          {
            message: titleCase(
              t(translations.office.localized_string_input.fields.rules.required) as string
            ),
          },
          {shouldFocus: true}
        );
      } else if (value?.length === 0 && value?.length < fieldMaxLength) {
        clearErrors(localizedField);
      }
    }

    // Max length validations
    if (value?.length) {
      if (value?.length >= fieldMaxLength) {
        setError(
          localizedField,
          {
            message: titleCase(
              t(translations.office.localized_string_input.fields.rules.max_length, {
                char: fieldMaxLength,
              }) as string
            ),
          },
          {shouldFocus: true}
        );
      } else {
        clearErrors(localizedField);
      }
    }
  };

  // Fill form async values
  useEffect(() => {
    if (mode === 'edit') {
      fillFormValues();
    }
  }, [fillFormValues, mode]);

  const permissions = watch('permissions');

  const setPermissionValues = (
    permissionTypes: RolePermissionType[],
    values: Partial<Pick<Permission, 'canRead' | 'canWrite'>>
  ) => {
    const currentPermissions = getValues('permissions');
    const updatedPermissions = {...currentPermissions};

    permissionTypes.forEach((type) => {
      const currentPermission = currentPermissions[type];

      if (currentPermission) {
        updatedPermissions[type] = {...currentPermission, ...values};
      }
    });

    reset(
      {...getValues(), permissions: updatedPermissions},
      {
        keepDefaultValues: true,
        keepErrors: true,
        keepTouched: true,
        keepIsValid: true,
      }
    );
  };

  const areAllPermissionsSet = (
    permissionTypes: RolePermissionType[],
    access: 'canRead' | 'canWrite'
  ) =>
    permissionTypes.length > 0 &&
    permissionTypes.every((type) => Boolean(permissions[type]?.[access]));

  const allPermissionsEnabled =
    areAllPermissionsSet(DEFAULT_PERMISSION_TYPES, 'canRead') &&
    areAllPermissionsSet(DEFAULT_PERMISSION_TYPES, 'canWrite');

  return (
    <Box className="role-permissions">
      <GenericHeader
        titleCrumbs={
          isRunningMobile
            ? [
                {
                  label: t(
                    translations.office.professional_caregiver.roles_and_permissions.generic_header
                      .breadcrumbs.roles_and_permissions
                  ),
                  link: '#',
                },
              ]
            : mode === 'edit'
              ? [
                  {
                    label: t(translations.screen_paths.professional_caregiver_team),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                  },
                  {
                    label: t(
                      translations.screen_paths.professional_caregiver_roles_and_permissions
                    ),
                    link: location.pathname.replace(`/${role?.id!}`, ''),
                  },
                  {label: role?.name!, link: location.pathname},
                ]
              : [
                  {
                    label: t(translations.screen_paths.professional_caregiver_team),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
                  },
                  {
                    label: t(
                      translations.screen_paths.professional_caregiver_roles_and_permissions
                    ),
                    link: location.pathname.replace(`/${role?.id!}`, ''),
                  },
                ]
        }
        isLoading={isLoadingRole}
        leftColumn={
          isLoadingRole ? (
            <Skeleton height={35} width={80} />
          ) : (
            <Typography className="left-column-subtitle" noWrap>
              {t(
                translations.role_permissions_professional_caregiver_office_screen.generic_header
                  .subtitle
              )}
            </Typography>
          )
        }
      />

      {isLoadingRole ? (
        <Card className="card-wrapper" elevation={0}>
          <LoadingContent />
        </Card>
      ) : (
        <>
          <Card className="card-wrapper" elevation={0}>
            <ContentPage>
              <Stack component="form" gap={4} sx={{width: '100%'}}>
                <Stack gap={4} sx={{paddingX: 2}}>
                  <LocalizedStringInput
                    defaultValueLabel={t(
                      translations.role_permissions_professional_caregiver_office_screen.fields.name
                        .label
                    )}
                    localizedValuesLabel={t(
                      translations.role_permissions_professional_caregiver_office_screen
                        .localized_string_input.name.title
                    )}
                    defaultValuePlaceholder={t(
                      translations.role_permissions_professional_caregiver_office_screen.fields.name
                        .placeholder
                    )}
                    defaultValue={getValues('name')}
                    localizedValues={getValues('nameLocalized')}
                    defaultValueError={errors?.name?.message}
                    localizedValuesErrors={
                      errors?.nameLocalized as Record<
                        keyof LocalizedString,
                        Record<'message', string>
                      >
                    }
                    localizedInputValidation={(value, language) =>
                      handleLocalizedInputValidation(value, 'name', language)
                    }
                    handleChangeDefaultValue={(name) => {
                      setValue('name', name, SET_VALUE_SIDE_EFFECTS);
                    }}
                    handleChangeLocalizedValues={(nameLocalized) =>
                      setValue('nameLocalized', nameLocalized, SET_VALUE_SIDE_EFFECTS)
                    }
                    defaultValueSx={{
                      '.MuiInputBase-root': {
                        fontSize: '22px',
                        fontWeight: 500,
                        lineHeight: '18px',
                        color: 'var(--dcm-gray)',
                        '&:after, &:before': {
                          borderBottom: `1px solid ${Pallete.gray.lightbg}`,
                        },
                        '&:hover, &:active': {
                          '&:after, &:before': {
                            borderBottom: `1px solid transparent`,
                          },
                        },
                      },
                    }}
                    defaultValueRequired
                  />
                  <LocalizedStringInput
                    defaultValueLabel={t(
                      translations.role_permissions_professional_caregiver_office_screen.fields
                        .description.label
                    )}
                    localizedValuesLabel={t(
                      translations.role_permissions_professional_caregiver_office_screen
                        .localized_string_input.description.title
                    )}
                    defaultValuePlaceholder={t(
                      translations.role_permissions_professional_caregiver_office_screen.fields
                        .description.placeholder
                    )}
                    defaultValueError={errors?.description?.message}
                    localizedValuesErrors={
                      errors?.descriptionLocalized as Record<
                        keyof LocalizedString,
                        Record<'message', string>
                      >
                    }
                    defaultValue={getValues('description')}
                    localizedValues={getValues('descriptionLocalized')}
                    localizedInputValidation={(value, language) =>
                      handleLocalizedInputValidation(value, 'description', language)
                    }
                    handleChangeDefaultValue={(description) =>
                      setValue('description', description, SET_VALUE_SIDE_EFFECTS)
                    }
                    handleChangeLocalizedValues={(descriptionLocalized) =>
                      setValue('descriptionLocalized', descriptionLocalized, SET_VALUE_SIDE_EFFECTS)
                    }
                    defaultValueSx={{
                      '.MuiInputBase-root': {
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '18px',
                        color: 'var(--dcm-gray)',
                        '&:after, &:before': {
                          borderBottom: `1px solid ${Pallete.gray.lightbg}`,
                        },
                        '&:hover, &:active': {
                          '&:after, &:before': {
                            borderBottom: `1px solid transparent`,
                          },
                        },
                      },
                    }}
                  />
                </Stack>

                <Box className="permissions-list-select-all">
                  <Typography className="permissions-list-select-all-label">
                    {t(
                      translations.role_permissions_professional_caregiver_office_screen
                        .select_all_permissions
                    )}
                  </Typography>
                  <Switch
                    aria-label={t(
                      translations.role_permissions_professional_caregiver_office_screen
                        .select_all_permissions
                    )}
                    checked={allPermissionsEnabled}
                    value={allPermissionsEnabled}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setPermissionValues(DEFAULT_PERMISSION_TYPES, {
                        canRead: checked,
                        canWrite: checked,
                      });
                    }}
                  />
                </Box>

                <List className="permissions-list">
                  {ORDERED_SECTIONS.map((sectionName, sectionIndex) => {
                    const reactKey = `permission-${sectionName}-${sectionIndex}`;
                    const sectionPermissions = SECTIONED_PERMISSIONS[sectionName];
                    const sectionPermissionTypes = sectionPermissions.map(({type}) => type);
                    return (
                      <CardCollapse
                        cardProps={{
                          className: 'permissions-list-section-wrapper',
                        }}
                        key={reactKey}
                        defaultExpand={false}
                        collapsableHeader={
                          <Grid container className="permissions-list-section-header">
                            <Grid item xs={6}>
                              <Typography
                                className={`permissions-list-section-name${!authSession?.selectedAffiliation?.contractsOverview?.includesDigitalInclusion && sectionName === 'digital-inclusion' ? '-access' : ''}`}
                                noWrap
                              >
                                {t(translations.role_permissions_section[sectionName])}
                              </Typography>
                            </Grid>
                            <Grid className="permissions-list-section-action-cell" item xs={3}>
                              <Tooltip
                                title={t(
                                  translations.role_permissions_professional_caregiver_office_screen
                                    .view.tooltip
                                )}
                              >
                                <Typography
                                  component="span"
                                  className="permissions-list-action-cell-label"
                                >
                                  {t(
                                    translations
                                      .role_permissions_professional_caregiver_office_screen.view
                                      .label
                                  )}
                                </Typography>
                              </Tooltip>
                              <Switch
                                aria-label={t(
                                  translations.role_permissions_professional_caregiver_office_screen
                                    .view.label
                                )}
                                checked={areAllPermissionsSet(sectionPermissionTypes, 'canRead')}
                                value={areAllPermissionsSet(sectionPermissionTypes, 'canRead')}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setPermissionValues(
                                    sectionPermissionTypes,
                                    checked ? {canRead: true} : {canRead: false, canWrite: false}
                                  );
                                }}
                              />
                            </Grid>
                            <Grid className="permissions-list-section-action-cell" item xs={3}>
                              <Tooltip
                                title={t(
                                  translations.role_permissions_professional_caregiver_office_screen
                                    .edit.tooltip
                                )}
                              >
                                <Typography
                                  component="span"
                                  className="permissions-list-action-cell-label"
                                >
                                  {t(
                                    translations
                                      .role_permissions_professional_caregiver_office_screen.edit
                                      .label
                                  )}
                                </Typography>
                              </Tooltip>
                              <Switch
                                aria-label={t(
                                  translations.role_permissions_professional_caregiver_office_screen
                                    .edit.label
                                )}
                                checked={areAllPermissionsSet(sectionPermissionTypes, 'canWrite')}
                                value={areAllPermissionsSet(sectionPermissionTypes, 'canWrite')}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setPermissionValues(
                                    sectionPermissionTypes,
                                    checked ? {canRead: true, canWrite: true} : {canWrite: false}
                                  );
                                }}
                              />
                            </Grid>
                          </Grid>
                        }
                      >
                        <List className="permissions-list-section-inner-list">
                          {Object.values(sectionPermissions).map(
                            (permission: Permission, permissionIndex) => {
                              const reactKey = `permission-${sectionName}-${permission.type}-${permissionIndex}`;
                              const crossPermission =
                                (!authSession?.selectedAffiliation?.contractsOverview
                                  ?.includesDigitalInclusion &&
                                  DIGITAL_INCLUSION_PERMISSIONS.includes(permission.type)) ||
                                (!authSession?.selectedAffiliation?.contractsOverview
                                  ?.includesDigitalCareManagement &&
                                  DIGITAL_CARE_MANAGEMENT_PERMISSIONS.includes(permission.type));

                              return (
                                <ListItem sx={{padding: 0}} key={reactKey}>
                                  <Grid container>
                                    <Grid
                                      className={`permissions-list-wrapper-cell${
                                        crossPermission ? '-access' : ''
                                      }`}
                                      item
                                      xs={6}
                                    >
                                      <Box>
                                        <Tooltip.NotAllowedIf
                                          title={
                                            <Trans
                                              values={{
                                                module: t(
                                                  translations.office.access[
                                                    DIGITAL_INCLUSION_PERMISSIONS.includes(
                                                      permission.type
                                                    )
                                                      ? 'digital_inclusion'
                                                      : 'care_management'
                                                  ]
                                                ),
                                              }}
                                            >
                                              {t(translations.office.access.tooltip)}
                                            </Trans>
                                          }
                                          condition={crossPermission}
                                        >
                                          <Typography noWrap>
                                            {t(translations.role_permissions[permission.type])}
                                          </Typography>
                                        </Tooltip.NotAllowedIf>
                                      </Box>
                                    </Grid>

                                    <Grid className="permissions-list-action-cell" item xs={3}>
                                      <input
                                        type="hidden"
                                        {...control.register(`permissions.${permission.type}.type`)}
                                        value={permission.type}
                                      />
                                      <Controller
                                        name={`permissions.${permission.type}.canRead`}
                                        control={control}
                                        render={({field: {onChange, value}}) => (
                                          <Switch
                                            {...{
                                              onChange: (e) => {
                                                const canWrite = getValues(
                                                  `permissions.${permission.type}.canWrite`
                                                );

                                                const val = Boolean(value);

                                                if (val && canWrite) {
                                                  setValue(
                                                    `permissions.${permission.type}.canWrite`,
                                                    false as never,
                                                    SET_VALUE_SIDE_EFFECTS
                                                  );
                                                }
                                                onChange(e);
                                              },
                                              checked: value,
                                              value,
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>
                                    <Grid className="permissions-list-action-cell" item xs={3}>
                                      <Controller
                                        name={`permissions.${permission.type}.canWrite`}
                                        control={control}
                                        render={({field: {onChange, value}}) => (
                                          <Switch
                                            {...{
                                              onChange: (e) => {
                                                const val = Boolean(value);
                                                if (val !== true) {
                                                  setValue(
                                                    `permissions.${permission.type}.canRead`,
                                                    true as never,
                                                    SET_VALUE_SIDE_EFFECTS
                                                  );
                                                }

                                                onChange(e);
                                              },
                                              checked: value,
                                              value,
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>
                                  </Grid>
                                </ListItem>
                              );
                            }
                          )}
                        </List>
                      </CardCollapse>
                    );
                  })}
                </List>
              </Stack>
            </ContentPage>
          </Card>
          <ContentPage>
            <Stack className="action-buttons-wrapper">
              <Button
                variant="text"
                color="error"
                disabled={!isDirty || isSubmitting}
                onClick={handleCancel}
              >
                {t(translations.office.generics.cancel)}
              </Button>
              <LoadingButton
                variant="contained"
                disabled={!isValid || !nameValue || isSubmitting}
                loadingPosition={isSubmitting ? 'start' : undefined}
                type="button"
                color="primary"
                sx={{minWidth: '100px'}}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {t(translations.office.generics.save)}
              </LoadingButton>
            </Stack>
          </ContentPage>
        </>
      )}
      <RouterPrompt
        when={isDirty && mode === 'edit' && !isSubmitting}
        title={t(
          translations.role_permissions_professional_caregiver_office_screen.router_prompt.title
        )}
        subtitle={t(
          translations.role_permissions_professional_caregiver_office_screen.router_prompt.content
            .title
        )}
        message={t(
          translations.role_permissions_professional_caregiver_office_screen.router_prompt.content
            .subtitle
        )}
        onOK={() => true}
        onCancel={() => false}
        toastNotification={{
          severity: 'info',
          message: t(
            translations.role_permissions_professional_caregiver_office_screen.snackbar.exit
          ),
        }}
      />
    </Box>
  );
};

export default RolePermissionsProfessionalCaregiverOfficeScreen;
